import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Percent, Gift, DownloadSimple, File, Trash, Plus, Buildings } from '@phosphor-icons/react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import Validation3DSection from './Validation3DSection';

// ============================================================================
// Établissement — composants partagés (fiche client + création utilisateur)
// ============================================================================

// Rendu de l'adresse complète d'un établissement, prêt pour étiquette d'envoi.
export function EstablishmentAddressLines({ est }) {
  if (!est) return null;
  const line1 = est.name;
  const line2 = est.address;
  const line3 = [est.code_postal, est.ville].filter(Boolean).join(' ');
  return (
    <div className="text-xs text-zinc-600 leading-tight" data-testid={`est-address-${est.id}`}>
      {line1 && <div className="font-medium text-zinc-800">{line1}</div>}
      {line2 && <div>{line2}</div>}
      {line3 && <div>{line3}</div>}
      {(est.phone || est.email) && (
        <div className="text-zinc-500 mt-0.5">
          {est.phone && <span>{est.phone}</span>}
          {est.phone && est.email && <span> · </span>}
          {est.email && <span>{est.email}</span>}
        </div>
      )}
    </div>
  );
}

// Formulaire inline pour créer OU éditer un établissement. Contrôlé via `value` + `onChange`.
// Ne gère PAS la persistance (le parent le fait).
export function EstablishmentFormFields({ value, onChange, testIdPrefix = 'est' }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
      <div className="sm:col-span-6">
        <Label className="label-overline">Nom de l'établissement *</Label>
        <Input value={value.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="Magasin Centre-ville" className="rounded-sm mt-1" data-testid={`${testIdPrefix}-name`} />
      </div>
      <div className="sm:col-span-6">
        <Label className="label-overline">Adresse *</Label>
        <Input value={value.address || ''} onChange={(e) => set('address', e.target.value)} placeholder="12 rue de la République" className="rounded-sm mt-1" data-testid={`${testIdPrefix}-address`} />
      </div>
      <div className="sm:col-span-2">
        <Label className="label-overline">Code postal *</Label>
        <Input value={value.code_postal || ''} onChange={(e) => set('code_postal', e.target.value)} placeholder="69000" className="rounded-sm mt-1" data-testid={`${testIdPrefix}-code-postal`} />
      </div>
      <div className="sm:col-span-4">
        <Label className="label-overline">Ville *</Label>
        <Input value={value.ville || ''} onChange={(e) => set('ville', e.target.value)} placeholder="Lyon" className="rounded-sm mt-1" data-testid={`${testIdPrefix}-ville`} />
      </div>
      <div className="sm:col-span-3">
        <Label className="label-overline">Téléphone</Label>
        <Input value={value.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="04 78 00 00 00" className="rounded-sm mt-1" data-testid={`${testIdPrefix}-phone`} />
      </div>
      <div className="sm:col-span-3">
        <Label className="label-overline">Email</Label>
        <Input type="email" value={value.email || ''} onChange={(e) => set('email', e.target.value)} placeholder="magasin@societe.fr" className="rounded-sm mt-1" data-testid={`${testIdPrefix}-email`} />
      </div>
    </div>
  );
}

// Validation minimale : name + address + code_postal + ville obligatoires.
export function isEstablishmentValid(est) {
  return !!(est && (est.name || '').trim() && (est.address || '').trim() && (est.code_postal || '').trim() && (est.ville || '').trim());
}

const EMPTY_ESTABLISHMENT = { name: '', address: '', code_postal: '', ville: '', phone: '', email: '' };

// Dialog dédié à l'édition d'un établissement existant (ouvert depuis la fiche client).
export function EstablishmentEditDialog({ open, onOpenChange, establishment, onSubmit }) {
  const [form, setForm] = React.useState(EMPTY_ESTABLISHMENT);
  React.useEffect(() => {
    if (open) setForm(establishment ? { ...EMPTY_ESTABLISHMENT, ...establishment } : EMPTY_ESTABLISHMENT);
  }, [open, establishment]);
  const submit = () => {
    if (!isEstablishmentValid(form)) {
      toast.error('Nom, adresse, code postal et ville sont obligatoires');
      return;
    }
    onSubmit(form);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="est-edit-dialog">
        <DialogHeader>
          <DialogTitle>{establishment?.id ? 'Modifier l\'établissement' : 'Nouvel établissement'}</DialogTitle>
          <DialogDescription>Adresse utilisée pour les étiquettes d'envoi.</DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <EstablishmentFormFields value={form} onChange={setForm} testIdPrefix="est-edit" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
          <Button onClick={submit} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="est-edit-submit">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ====== Add User Dialog ======
export function AddUserDialog({
  open, onOpenChange, form, setForm, companies, establishments, onSubmit, onCreateEstablishment
}) {
  // Formulaire inline pour créer un nouvel établissement à la volée.
  const [showNewEst, setShowNewEst] = React.useState(false);
  const [newEst, setNewEst] = React.useState(EMPTY_ESTABLISHMENT);
  const [creatingEst, setCreatingEst] = React.useState(false);
  const isClient = form.role === 'client';
  const hasCompany = !!(form.company_id || (form.company_name || '').trim());
  const hasEstablishment = (form.establishment_ids || []).length > 0;

  React.useEffect(() => {
    if (!open) { setShowNewEst(false); setNewEst(EMPTY_ESTABLISHMENT); }
  }, [open]);

  const handleCreateEst = async () => {
    if (!isEstablishmentValid(newEst)) {
      toast.error('Nom, adresse, code postal et ville sont obligatoires');
      return;
    }
    if (!form.company_id) {
      toast.error('Sélectionnez une société existante avant de créer un établissement. Pour une nouvelle société, créez d\'abord l\'utilisateur (la société sera créée) puis ajoutez les établissements depuis la fiche.');
      return;
    }
    setCreatingEst(true);
    try {
      const created = await onCreateEstablishment({ ...newEst, company_id: form.company_id });
      if (created?.id) {
        setForm({ ...form, establishment_ids: [...(form.establishment_ids || []), created.id] });
        setShowNewEst(false);
        setNewEst(EMPTY_ESTABLISHMENT);
        toast.success('Établissement créé et rattaché');
      }
    } finally { setCreatingEst(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ajouter un utilisateur</DialogTitle>
          <DialogDescription>Créer un nouveau compte utilisateur</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="label-overline">Nom *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-sm mt-1" data-testid="new-user-name" /></div>
          <div><Label className="label-overline">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-sm mt-1" data-testid="new-user-email" /></div>
          <div><Label className="label-overline">Mot de passe *</Label><Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="rounded-sm mt-1" data-testid="new-user-password" /></div>
          <div>
            <Label className="label-overline">Rôle</Label>
            <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
              <SelectTrigger className="rounded-sm mt-1 bg-white" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="client">Client</SelectItem><SelectItem value="technician">Technicien</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="label-overline">Société{isClient ? ' *' : ''}</Label>
            <Select value={form.company_id} onValueChange={(v) => {
              const comp = companies.find(c => c.id === v);
              setForm({...form, company_id: v, company_name: comp ? comp.name : form.company_name, establishment_ids: []});
            }}>
              <SelectTrigger className="rounded-sm mt-1 bg-white" data-testid="new-user-company-select"><SelectValue placeholder="Sélectionner une société" /></SelectTrigger>
              <SelectContent className="bg-white">
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.account_number ? `(${c.account_number})` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Ou créer une nouvelle société" value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value, company_id: ''})} className="rounded-sm mt-1" data-testid="new-user-company" />
          </div>
          {isClient && (
            <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-sm space-y-3" data-testid="new-user-establishments-section">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Établissements rattachés *</p>
                {form.company_id && !showNewEst && (
                  <Button size="sm" variant="outline" className="text-xs rounded-sm h-7" onClick={() => setShowNewEst(true)} data-testid="new-user-add-est-btn">
                    <Plus size={12} className="mr-1" /> Nouveau
                  </Button>
                )}
              </div>
              {!form.company_id && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                  {(form.company_name || '').trim()
                    ? "Nouvelle société : créez l'utilisateur d'abord, puis ajoutez les établissements depuis sa fiche."
                    : 'Sélectionnez ou créez d\'abord une société.'}
                </p>
              )}
              {form.company_id && establishments.map(est => (
                <label key={est.id} className={`flex items-start gap-2 p-2 rounded-sm border cursor-pointer transition-colors ${(form.establishment_ids || []).includes(est.id) ? 'border-zinc-900 bg-white' : 'border-zinc-200 hover:border-zinc-400 bg-white'}`} data-testid={`new-user-est-toggle-${est.id}`}>
                  <input type="checkbox" checked={(form.establishment_ids || []).includes(est.id)} onChange={(e) => {
                    const ids = form.establishment_ids || [];
                    setForm({...form, establishment_ids: e.target.checked ? [...ids, est.id] : ids.filter(i => i !== est.id)});
                  }} className="w-4 h-4 rounded border-zinc-300 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{est.name}</div>
                    <EstablishmentAddressLines est={est} />
                  </div>
                </label>
              ))}
              {form.company_id && establishments.length === 0 && !showNewEst && (
                <p className="text-xs text-zinc-500">Aucun établissement pour cette société — cliquez sur « Nouveau » pour en créer un.</p>
              )}
              {showNewEst && (
                <div className="bg-white border border-zinc-300 rounded-sm p-3 space-y-3" data-testid="new-user-new-est-form">
                  <EstablishmentFormFields value={newEst} onChange={setNewEst} testIdPrefix="new-user-new-est" />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { setShowNewEst(false); setNewEst(EMPTY_ESTABLISHMENT); }}>Annuler</Button>
                    <Button size="sm" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white" onClick={handleCreateEst} disabled={creatingEst} data-testid="new-user-new-est-submit">
                      {creatingEst ? 'Création...' : 'Créer et rattacher'}
                    </Button>
                  </div>
                </div>
              )}
              {isClient && hasCompany && !hasEstablishment && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm p-2" data-testid="new-user-est-required-warning">
                  Un client doit être rattaché à au moins un établissement (adresse d'expédition).
                </p>
              )}
            </div>
          )}
          {form.role === 'client' && (
            <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm space-y-3">
              <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Droits d'accès</p>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="new-user-can-view-prices">
                <input type="checkbox" checked={form.can_view_prices} onChange={(e) => setForm({...form, can_view_prices: e.target.checked})} className="w-4 h-4 rounded border-zinc-300" />
                <span className="text-sm">Accès aux tarifs</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="new-user-can-view-terms">
                <input type="checkbox" checked={form.can_view_commercial_terms} onChange={(e) => setForm({...form, can_view_commercial_terms: e.target.checked})} className="w-4 h-4 rounded border-zinc-300" />
                <span className="text-sm">Accès aux conditions commerciales (remises)</span>
              </label>
            </div>
          )}
          <div className="grid grid-cols-4 gap-2">
            <div><Label className="label-overline">N°</Label><Input placeholder="12" value={form.address_number} onChange={(e) => setForm({...form, address_number: e.target.value})} className="rounded-sm mt-1" data-testid="new-user-addr-num" /></div>
            <div className="col-span-3"><Label className="label-overline">Rue</Label><Input placeholder="rue de la République" value={form.address_street} onChange={(e) => setForm({...form, address_street: e.target.value})} className="rounded-sm mt-1" data-testid="new-user-addr-street" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="label-overline">Code postal</Label><Input placeholder="69000" value={form.address_zip} onChange={(e) => setForm({...form, address_zip: e.target.value})} className="rounded-sm mt-1" data-testid="new-user-addr-zip" /></div>
            <div className="col-span-2"><Label className="label-overline">Ville</Label><Input placeholder="Lyon" value={form.address_city} onChange={(e) => setForm({...form, address_city: e.target.value})} className="rounded-sm mt-1" data-testid="new-user-addr-city" /></div>
          </div>
          <div><Label className="label-overline">Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="rounded-sm mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
          <Button onClick={onSubmit} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="confirm-add-user" disabled={isClient && (!hasCompany || !hasEstablishment)}>Créer l'utilisateur</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== Edit User Dialog ======
export function EditUserDialog({
  open, onOpenChange, form, setForm, companies, establishments, onSubmit
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Modifier l'utilisateur</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="label-overline">Nom</Label><Input value={form.name || ''} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-sm mt-1" /></div>
          <div><Label className="label-overline">Email</Label><Input value={form.email || ''} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-sm mt-1" /></div>
          <div><Label className="label-overline">Société</Label>
            <Select value={form.company_id || ''} onValueChange={(v) => {
              const comp = companies.find(c => c.id === v);
              setForm({...form, company_id: v, company_name: comp ? comp.name : form.company_name, establishment_ids: []});
            }}>
              <SelectTrigger className="rounded-sm mt-1 bg-white"><SelectValue placeholder="Sélectionner une société" /></SelectTrigger>
              <SelectContent className="bg-white">
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.account_number ? `(${c.account_number})` : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Ou créer une nouvelle société" value={form.company_name || ''} onChange={(e) => setForm({...form, company_name: e.target.value, company_id: ''})} className="rounded-sm mt-1" />
          </div>
          {form.company_id && (
            <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-sm space-y-2">
              <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Établissements associés</p>
              {establishments.map(est => (
                <label key={est.id} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={(form.establishment_ids || []).includes(est.id)} onChange={(e) => {
                    const ids = form.establishment_ids || [];
                    setForm({...form, establishment_ids: e.target.checked ? [...ids, est.id] : ids.filter(i => i !== est.id)});
                  }} className="w-4 h-4 rounded border-zinc-300" />
                  <span className="text-sm">{est.name}</span>
                  {est.address && <span className="text-xs text-zinc-400">— {est.address}</span>}
                </label>
              ))}
              {establishments.length === 0 && <p className="text-xs text-zinc-400">Aucun établissement</p>}
            </div>
          )}
          <div className="grid grid-cols-4 gap-2">
            <div><Label className="label-overline">N°</Label><Input placeholder="12" value={form.address_number || ''} onChange={(e) => setForm({...form, address_number: e.target.value})} className="rounded-sm mt-1" /></div>
            <div className="col-span-3"><Label className="label-overline">Rue</Label><Input placeholder="rue de la République" value={form.address_street || ''} onChange={(e) => setForm({...form, address_street: e.target.value})} className="rounded-sm mt-1" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="label-overline">Code postal</Label><Input placeholder="69000" value={form.address_zip || ''} onChange={(e) => setForm({...form, address_zip: e.target.value})} className="rounded-sm mt-1" /></div>
            <div className="col-span-2"><Label className="label-overline">Ville</Label><Input placeholder="Lyon" value={form.address_city || ''} onChange={(e) => setForm({...form, address_city: e.target.value})} className="rounded-sm mt-1" /></div>
          </div>
          <div><Label className="label-overline">Téléphone</Label><Input value={form.phone || ''} onChange={(e) => setForm({...form, phone: e.target.value})} className="rounded-sm mt-1" /></div>
          <div>
            <Label className="label-overline">Rôle</Label>
            <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
              <SelectTrigger className="rounded-sm mt-1 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="client">Client</SelectItem><SelectItem value="technician">Technicien</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
            </Select>
          </div>
          {form.role === 'client' && (
            <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-sm space-y-3">
              <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Droits d'accès</p>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="edit-user-can-view-prices">
                <input type="checkbox" checked={form.can_view_prices || false} onChange={(e) => setForm({...form, can_view_prices: e.target.checked})} className="w-4 h-4 rounded border-zinc-300" />
                <span className="text-sm">Accès aux tarifs</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="edit-user-can-view-terms">
                <input type="checkbox" checked={form.can_view_commercial_terms || false} onChange={(e) => setForm({...form, can_view_commercial_terms: e.target.checked})} className="w-4 h-4 rounded border-zinc-300" />
                <span className="text-sm">Accès aux conditions commerciales (remises)</span>
              </label>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button><Button onClick={onSubmit} className="rounded-sm bg-zinc-900 hover:bg-zinc-800">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== Client Detail Dialog ======
export function ClientDetailDialog({
  open, onOpenChange, user, orderCount, companyEstablishments, onToggleEstablishment, onEdit, onDiscount, onPromoInvite, onCreateEstablishment, onEditEstablishment, onDeleteEstablishment, onCompanyMarquage
}) {
  if (!user) return null;
  const promoStatus = user.promo_status;
  const promoLabels = {
    invited: { text: 'Invitation envoyée', cls: 'bg-amber-50 text-amber-700' },
    active: { text: 'Promo active', cls: 'bg-emerald-50 text-emerald-700' },
    expired: { text: 'Promo expirée', cls: 'bg-zinc-100 text-zinc-500' },
    converted: { text: 'Converti', cls: 'bg-blue-50 text-blue-700' },
  };
  const promoLabel = promoLabels[promoStatus];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fiche client — {user.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Nom</p>
              <p className="text-sm font-medium">{user.name}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Email</p>
              <p className="text-sm">{user.email}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Société</p>
              <p className="text-sm">{user.company_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Commandes</p>
              <p className="text-sm font-medium">{orderCount}</p>
            </div>
          </div>

          {/* Statut promo */}
          <div className="border border-zinc-200 rounded-sm p-3" data-testid="client-promo-section">
            <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Offre promotionnelle</p>
            {promoLabel ? (
              <div className="space-y-2">
                <span className={`inline-block text-xs px-2 py-1 rounded-sm font-medium ${promoLabel.cls}`} data-testid="client-promo-status">
                  {promoLabel.text}
                </span>
                {promoStatus === 'active' && (
                  <div className="text-xs text-zinc-600 space-y-0.5">
                    <p>Quota : {user.promo_quota_used || 0} / {user.promo_quota_total || 10} embouts utilisés</p>
                    {user.promo_end_date && <p>Expire le : {new Date(user.promo_end_date).toLocaleDateString('fr-FR')}</p>}
                  </div>
                )}
                {promoStatus === 'invited' && user.promo_invited_at && (
                  <p className="text-xs text-zinc-500">Envoyée le {new Date(user.promo_invited_at).toLocaleDateString('fr-FR')}</p>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">Aucune offre en cours</span>
                <Button size="sm" variant="outline" className="text-xs rounded-sm" onClick={() => onPromoInvite && onPromoInvite(user)} data-testid="client-detail-promo-btn">
                  <Gift size={14} className="mr-1.5" /> Inviter à l'offre
                </Button>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-zinc-400 uppercase tracking-wider">Établissements assignés</p>
              {user.company_id && onCreateEstablishment && (
                <Button size="sm" variant="outline" className="text-xs rounded-sm h-7" onClick={() => onCreateEstablishment(user)} data-testid="client-detail-add-est-btn">
                  <Plus size={12} className="mr-1" /> Nouveau
                </Button>
              )}
            </div>
            {companyEstablishments.length > 0 ? (
              <div className="space-y-2">
                {companyEstablishments.map(est => {
                  const isAssigned = (user.establishment_ids || []).includes(est.id);
                  return (
                    <div key={est.id} className={`p-2.5 rounded-sm border transition-colors ${isAssigned ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200'}`} data-testid={`est-card-${est.id}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox checked={isAssigned} onCheckedChange={() => onToggleEstablishment(est.id)} data-testid={`est-toggle-${est.id}`} className="mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <EstablishmentAddressLines est={est} />
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {onEditEstablishment && (
                            <button type="button" onClick={() => onEditEstablishment(est)} className="p-1.5 rounded-sm hover:bg-zinc-200 text-zinc-600 transition-colors" data-testid={`est-edit-${est.id}`} title="Modifier">
                              <Pencil size={14} />
                            </button>
                          )}
                          {onDeleteEstablishment && (
                            <button type="button" onClick={() => onDeleteEstablishment(est)} className="p-1.5 rounded-sm hover:bg-red-100 text-red-600 transition-colors" data-testid={`est-delete-${est.id}`} title="Supprimer">
                              <Trash size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-zinc-400">Aucun établissement lié à cette société.</p>
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t border-zinc-200">
            <Button variant="outline" className="flex-1" onClick={onEdit} data-testid="client-detail-edit-btn">
              <Pencil size={14} className="mr-2" /> Modifier
            </Button>
            <Button variant="outline" className="flex-1" onClick={onDiscount} data-testid="client-detail-discount-btn">
              <Percent size={14} className="mr-2" /> Conditions commerciales
            </Button>
          </div>

          {user.company_id && onCompanyMarquage && (
            <div className="pt-2">
              <Button
                variant="outline"
                className="w-full text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                onClick={() => onCompanyMarquage(user)}
                data-testid="client-detail-company-marquage-btn"
              >
                <Buildings size={14} className="mr-2" /> Marquage société (hérité par tous les utilisateurs)
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ====== Promo Invite Confirmation Dialog ======
export function PromoInviteDialog({ open, onOpenChange, user, onConfirm, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md max-h-[85vh] overflow-y-auto" data-testid="promo-invite-dialog">
        <DialogHeader>
          <DialogTitle>Offre d'essai client</DialogTitle>
          <DialogDescription>
            {user && <span className="block mt-1 font-medium">{user.name} — {user.company_name || user.email}</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-zinc-700">
            Cette action va envoyer une invitation permettant au client de bénéficier de :
          </p>
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4">
            <p className="font-semibold text-base text-zinc-900 mb-2">10 embouts offerts pendant 30 jours</p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Produits concernés :</p>
            <ul className="text-sm text-zinc-700 space-y-1 list-disc list-inside">
              <li>Embouts auditifs</li>
              <li>Obturateurs non filtrés</li>
              <li>Obturateurs anti-eau</li>
            </ul>
          </div>
          {user && !user.account_activated && (
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-sm">
              Ce client n'a pas encore activé son compte. Un lien d'activation lui sera envoyé avec l'offre.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
          <Button onClick={onConfirm} disabled={loading} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="promo-invite-confirm">
            {loading ? 'Envoi...' : 'Confirmer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== Discount Dialog ======
export function DiscountDialog({
  open, onOpenChange, user, products, form, setForm, onSubmit
}) {
  const [bulkValue, setBulkValue] = React.useState('');

  const applyBulk = () => {
    const v = parseFloat(bulkValue);
    if (isNaN(v) || v < 0 || v > 100) {
      return;
    }
    const next = {};
    for (const p of products) {
      next[p.id] = v;
    }
    setForm({ ...form, ...next });
  };

  const resetAll = () => {
    const next = { ...form };
    for (const p of products) {
      next[p.id] = 0;
    }
    setForm(next);
    setBulkValue('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Conditions commerciales — {user?.name}</DialogTitle>
          <DialogDescription>
            {user?.company_name && <span className="block">{user.company_name}</span>}
            Définir le pourcentage de remise pour chaque produit
          </DialogDescription>
        </DialogHeader>

        {/* Global bulk apply */}
        {products.length > 0 && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 flex items-center gap-2" data-testid="discount-bulk-row">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-zinc-700">Remise globale</p>
              <p className="text-[11px] text-zinc-500">S'applique à toutes les lignes ci-dessous</p>
            </div>
            <div className="flex items-center gap-1 w-24 flex-shrink-0">
              <Input
                type="number" min="0" max="100" step="1"
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
                className="rounded-sm text-center h-9 bg-white"
                placeholder="0"
                data-testid="discount-bulk-input"
              />
              <span className="text-sm text-zinc-500 flex-shrink-0">%</span>
            </div>
            <Button onClick={applyBulk} disabled={bulkValue === ''} size="sm" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 h-9" data-testid="discount-bulk-apply">
              Appliquer
            </Button>
            <Button onClick={resetAll} variant="outline" size="sm" className="rounded-sm h-9" data-testid="discount-bulk-reset" title="Tout remettre à 0">
              Reset
            </Button>
          </div>
        )}

        <div className="space-y-3">
          {products.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">Aucun produit dans le catalogue. Ajoutez d'abord des produits dans l'onglet Produits.</p>
          ) : (
            products.map((product) => (
              <div key={product.id} className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{product.name}</p>
                  <p className="text-xs text-zinc-500">{product.base_price?.toFixed(2)} € — {product.category}</p>
                </div>
                <div className="flex items-center gap-1 w-24 flex-shrink-0">
                  <Input
                    type="number" min="0" max="100" step="1"
                    value={form[product.id] || 0}
                    onChange={(e) => setForm({...form, [product.id]: parseFloat(e.target.value) || 0})}
                    className="rounded-sm text-center h-9"
                    data-testid={`discount-product-${product.id}`}
                  />
                  <span className="text-sm text-zinc-500 flex-shrink-0">%</span>
                </div>
              </div>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
          <Button onClick={onSubmit} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="save-discounts-btn">Enregistrer les conditions</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== Product Dialog ======
export function ProductDialog({
  open, onOpenChange, editing, form, setForm, onSubmit
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Modifier le produit' : 'Ajouter un produit'}</DialogTitle>
          <DialogDescription>{editing ? 'Modifier les informations du produit' : 'Créer un nouveau produit dans le catalogue'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="label-overline">Nom du produit *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-sm mt-1" data-testid="product-name" /></div>
          <div><Label className="label-overline">Description</Label><textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full p-3 border border-zinc-200 rounded-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-zinc-900 mt-1 text-sm" data-testid="product-description" /></div>
          <div>
            <Label className="label-overline">Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
              <SelectTrigger className="rounded-sm mt-1 bg-white" data-testid="product-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="embout">Embout</SelectItem>
                <SelectItem value="obturateur">Obturateur</SelectItem>
                <SelectItem value="option">Option</SelectItem>
                <SelectItem value="accessoire">Accessoire</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-overline">Prix de base</Label>
            <div className="relative">
              <Input type="number" step="0.01" value={form.base_price} onChange={(e) => setForm({...form, base_price: parseFloat(e.target.value) || 0})} className="rounded-sm mt-1 pr-8" data-testid="product-price" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">&euro;</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
          <Button onClick={onSubmit} className="rounded-sm bg-zinc-900 hover:bg-zinc-800" data-testid="save-product-btn">{editing ? 'Enregistrer' : 'Créer le produit'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== Price Breakdown Dialog ======
export function PriceBreakdownDialog({ open, onOpenChange, order }) {
  if (!order) return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-[95vw] sm:max-w-lg" data-testid="price-breakdown-dialog">
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const opts = order.options || {};
  const isBilateral = order.ear_side === 'both';
  const unitPrice = order.product_price || 0;
  const finalUnitPrice = order.product_final_price || 0;

  const EarEmboutBlock = ({ side, label, serial, forme, matiere }) => (
    <div className="bg-zinc-50 p-4 rounded-sm border border-zinc-200" data-testid={`price-ear-${side}`}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium">Embout {label} — {order.product_name}</span>
        <span className="font-mono text-sm font-bold">{unitPrice.toFixed(2)} €</span>
      </div>
      <p className="text-xs text-zinc-500">
        {forme ? `Forme: ${forme.replace(/_/g, ' ')}` : ''}{forme && matiere ? ' | ' : ''}{matiere ? `Matière: ${matiere === 'resine_3d' ? 'Résine 3D' : matiere === 'silicone' ? 'Silicone' : matiere}` : ''}
        {serial ? ` | N°: ${serial}` : ''}
      </p>
      {order.discount_pct > 0 && (
        <div className="flex justify-between items-center mt-2 text-emerald-700">
          <span className="text-xs">Remise client ({order.discount_pct}%)</span>
          <span className="font-mono text-xs">-{(unitPrice * order.discount_pct / 100).toFixed(2)} €</span>
        </div>
      )}
      <div className="flex justify-between items-center mt-1 pt-1 border-t border-zinc-200">
        <span className="text-xs font-medium">Sous-total embout {label.toLowerCase()}</span>
        <span className="font-mono text-sm font-bold">{finalUnitPrice.toFixed(2)} €</span>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-[95vw] sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="price-breakdown-dialog">
        <DialogHeader>
          <DialogTitle>Détail tarifaire</DialogTitle>
          <DialogDescription>
            {`${order.patient_reference} — ${order.client_name}`}
            {isBilateral && <span className="block text-xs mt-1 font-medium text-zinc-700">Commande bilatérale — 2 embouts</span>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {isBilateral ? (
            <>
              <EarEmboutBlock
                side="od"
                label="OD (Droit)"
                serial={order.serial_number_right}
                forme={opts.forme_embout_od || opts.forme_embout}
                matiere={opts.matiere_od || order.material}
              />
              <EarEmboutBlock
                side="og"
                label="OG (Gauche)"
                serial={order.serial_number_left}
                forme={opts.forme_embout_og || opts.forme_embout}
                matiere={opts.matiere_og || order.material}
              />
            </>
          ) : (
            <div className="bg-zinc-50 p-4 rounded-sm border border-zinc-200" data-testid="price-ear-single">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Embout {order.ear_side === 'left' ? 'OG' : 'OD'} — {order.product_name}</span>
                <span className="font-mono text-sm font-bold" data-testid="price-product">{unitPrice.toFixed(2)} €</span>
              </div>
              <p className="text-xs text-zinc-500">Forme: {opts.forme_embout || '—'} | Matière: {order.material || '—'}</p>
              {order.discount_pct > 0 && (
                <div className="flex justify-between items-center mt-2 text-emerald-700">
                  <span className="text-xs">Remise client ({order.discount_pct}%)</span>
                  <span className="font-mono text-xs">-{(unitPrice * order.discount_pct / 100).toFixed(2)} €</span>
                </div>
              )}
              <div className="flex justify-between items-center mt-1 pt-1 border-t border-zinc-200">
                <span className="text-xs font-medium">Sous-total embout</span>
                <span className="font-mono text-sm font-bold" data-testid="price-product-final">{finalUnitPrice.toFixed(2)} €</span>
              </div>
            </div>
          )}

          {order.selected_options && order.selected_options.length > 0 && (
            <div className="bg-zinc-50 p-4 rounded-sm border border-zinc-200">
              <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-2">Options</p>
              <div className="space-y-1">
                {order.selected_options.map((opt, i) => (
                  <div key={opt.lookup_key || opt.name || i} className="flex justify-between items-center">
                    <span className="text-sm">{opt.name}</span>
                    <div className="flex items-center gap-2">
                      {opt.discount_pct > 0 && <span className="text-xs text-emerald-600">(-{opt.discount_pct}%)</span>}
                      <span className="font-mono text-sm" data-testid={`price-option-${i}`}>{(opt.final_price || 0).toFixed(2)} €</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t border-zinc-200">
                <span className="text-xs font-medium">Total options</span>
                <span className="font-mono text-sm font-bold" data-testid="price-options-total">{(order.options_total || 0).toFixed(2)} €</span>
              </div>
            </div>
          )}

          {(!order.selected_options || order.selected_options.length === 0) && (
            <div className="text-xs text-zinc-400 italic">Aucune option sélectionnée</div>
          )}

          <div className="bg-zinc-900 text-white p-4 rounded-sm">
            <div className="flex justify-between items-center">
              <span className="font-medium">TOTAL COMMANDE</span>
              <span className="font-mono text-xl font-bold" data-testid="price-total">{(order.final_price || 0).toFixed(2)} €</span>
            </div>
            {order.order_type === 'refabrication_free' && (
              <p className="text-xs text-emerald-400 mt-1">Refabrication gratuite — prix à 0</p>
            )}
            {isBilateral && (
              <p className="text-xs text-zinc-400 mt-1">Embout x2 ({finalUnitPrice.toFixed(2)} € × 2) + Options</p>
            )}
          </div>

          <p className="text-xs text-zinc-500">
            Côté: {order.ear_side === 'left' ? 'Gauche' : order.ear_side === 'right' ? 'Droit' : 'Bilatéral'}
          </p>

          {/* Fichiers STL */}
          {order.stl_files && order.stl_files.length > 0 && (
            <div className="bg-zinc-50 p-4 rounded-sm border border-zinc-200" data-testid="stl-files-section">
              <p className="text-xs font-semibold text-zinc-700 uppercase tracking-wider mb-2">Fichiers STL d'empreintes</p>
              <div className="space-y-2">
                {order.stl_files.map((f, i) => (
                  <div key={f.file_id || i} className="flex items-center justify-between bg-white p-2 rounded-sm border border-zinc-200">
                    <div className="flex items-center gap-2 min-w-0">
                      <File size={16} className="text-zinc-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{f.custom_filename || f.filename || 'fichier.stl'}</p>
                        <p className="text-[10px] text-zinc-400">{f.side === 'left' ? 'Gauche (L)' : f.side === 'right' ? 'Droit (R)' : f.side}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      className="h-8 px-2 text-zinc-600 hover:text-zinc-900"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const r = await axios.get(`${API}/files/${f.file_id}/download`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(r.data);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = r.headers['x-filename'] || f.custom_filename || f.filename || 'fichier.stl';
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch { toast.error('Erreur de téléchargement'); }
                      }}
                      data-testid={`download-stl-${f.file_id || i}`}
                    >
                      <DownloadSimple size={16} className="mr-1" />
                      <span className="text-xs">Télécharger</span>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Validation 3D Section */}
          {order.id && (
            <Validation3DSection orderId={order.id} order={order} />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== Delete Order Dialog ======
export function DeleteOrderDialog({ open, onOpenChange, order, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="delete-order-dialog">
        <DialogHeader>
          <DialogTitle>Supprimer cette commande ?</DialogTitle>
          <DialogDescription>
            {order && (
              <>
                <span className="block mt-2">Réf. patient : <strong>{order.patient_reference}</strong></span>
                <span className="block">Client : {order.client_name}</span>
                <span className="block">Statut : {order.status === 'completed' ? 'Terminée' : order.status === 'pending' ? 'En attente' : order.status === 'in_progress' ? 'Fabrication' : order.status === 'cancelled' ? 'Annulée' : order.status}</span>
                <span className="block mt-2 text-rose-600 font-medium">Cette action est irréversible. La commande sera retirée de toutes les listes et statistiques.</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
          <Button onClick={onConfirm} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-order">Supprimer définitivement</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== Delete Product Dialog ======
export function DeleteProductDialog({ open, onOpenChange, product, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="delete-product-dialog">
        <DialogHeader>
          <DialogTitle>Supprimer ce produit ?</DialogTitle>
          <DialogDescription>
            {product && (
              <>
                <span className="block mt-2">Produit : <strong>{product.name}</strong></span>
                <span className="block">Catégorie : {product.category}</span>
                <span className="block mt-2 text-zinc-600">S'il a déjà été utilisé dans des commandes, il sera archivé pour conserver l'historique. Sinon, il sera supprimé définitivement.</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
          <Button onClick={onConfirm} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-product">Supprimer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
